import type { Currency } from "@loot-os/api-contract";
import checkIcon from "@loot-os/icons/icons/check";
import { type MouseEventHandler, type ReactNode, useId } from "react";

import { Avatar } from "../core/Avatar.js";
import { cardStyles } from "../core/Card.styles.js";
import { Icon } from "../core/Icon.js";
import { AppLink } from "../foundations/AppLink.js";
import { cn } from "../foundations/cn.js";
import { Amount } from "./Amount.js";
import { ItemArt } from "./ItemArt.js";
import { NoticeBadge, type NoticeBadgeProps } from "./NoticeBadge.js";
import { OddsTag, type TierMarker } from "./OddsTag.js";
import { RarityEffects } from "./RarityEffects.js";
import { RarityName } from "./RarityName.js";
import { RARITY_FILL, type RarityTier } from "./rarity.js";
import { useRarityWake } from "./useRarityWake.js";

/** The widest a pull's art is drawn, in CSS px: a rail's card at its widest, less its margins. */
const PULL_ART_WIDTH = 120;
/** Art on a card wearing its rarity grows a little as the card wakes. */
const EFFECTS_ART_MOTION =
  "motion-safe:transition-transform motion-safe:duration-slow motion-safe:ease-standard motion-safe:group-hover/item:scale-[1.06]";
/** The widest a compact pull's art is drawn, in CSS px. */
const COMPACT_ART_WIDTH = 88;

/**
 * Something the shopper must know about an item, as a badge on its art that opens the whole
 * reason (NoticeBadge): a counted supply ("3 left"), that it can no longer ship.
 */
export type ItemNotice = Omit<NoticeBadgeProps, "className" | "triggerLabel">;

interface ItemCardBase {
  name: string;
  image: string;
  /** The art's slot: a trading card, or a square for anything else. Art of any shape fits it. */
  shape?: "card" | "square";
  /** Overrides the CurrencyProvider's currency for the value. */
  currency?: Currency;
  className?: string;
}

/** One possible item in a box's contents: what it is worth and its published chance. */
export interface DropItemCardProps extends ItemCardBase {
  variant: "drop";
  tier: RarityTier;
  /** The chance in percent, as published. */
  chance: number;
  /** Integer cents. */
  value: number;
  /** The tier's name for screen readers, in tenant copy. */
  tierLabel?: string;
  /** The tier beside the chance: its dot (the default) or its shape. */
  marker?: TierMarker;
  /**
   * Badges on the art's top corner, loudest first, each opening what it means: how many are left,
   * that it can no longer ship. On the art, so the card is the height of every other.
   */
  notices?: readonly ItemNotice[];
  /** Dress the card in its tier (glow, halftone, band, sheen). Off: only the chase tier's edge. */
  effects?: boolean;
  /** Opens the item's details. Without it the card is static. */
  onSelect?: MouseEventHandler<HTMLButtonElement>;
}

/** An item the shopper holds, or held. */
export interface OwnedItemCardProps extends ItemCardBase {
  variant: "owned";
  /** What it's worth in cents, or the words for what became of it ("Sold back", "Shipping to you"). */
  note: { kind: "value"; cents: number } | { kind: "text"; text: string };
  /**
   * Something standing in its way, as a short status on the art ("Can't ship") that opens the
   * full sentence: why, and what's left to do. The card keeps its height either way.
   */
  issue?: { label: string; text: string };
  /** Drawn faded: the shopper no longer holds it. */
  faded?: boolean;
  /** Pickable (selling back, building a forge box): the card becomes a toggle button. */
  selection?: { selected: boolean; onToggle: () => void };
}

/** A pull worth showing off: what someone got from a box. */
export interface PullItemCardProps extends ItemCardBase {
  variant: "pull";
  /** `regular` dressed in its rarity; `compact` a small card in a strip, its tier a dot. */
  size?: "regular" | "compact";
  /** What it's worth, in cents. */
  cents: number;
  tier: RarityTier;
  /** The tier's name as the store says it ("Legendary"). */
  tierLabel: string;
  /** Who pulled it, on a pull from the store's live feed: their name and picture (regular). */
  player?: { name: string; avatarUrl?: string };
  /** Pressing the card: opens the pull's details. */
  onSelect?: () => void;
  /** Where the card links instead: the box it came from (regular). */
  href?: string;
  /**
   * The accessible name, which has to contain what the card shows in its order (WCAG 2.5.3): "View
   * Umbreon $578.00 Legendary". Without it, the card is named by its words.
   */
  label?: string;
  /** The card's place in its row: where its rarity effects are in their motion. */
  seed?: number;
  /**
   * Dress the card in its tier: on for `regular` unless turned off, off for `compact` unless turned
   * on (a compact card wearing it insets its art so the glow shows).
   */
  effects?: boolean;
  /** Load its art first: one of the first cards a page shows. */
  priority?: boolean;
}

export type ItemCardProps = DropItemCardProps | OwnedItemCardProps | PullItemCardProps;

/**
 * One item, as a card, in the variant for what the shopper is looking at: `drop`, an item a box can hold, with its chance and value; `owned`, an
 * item the shopper holds, pickable when they are building a list; `pull`, an item someone got,
 * worth showing off. The art fits its slot at any orientation (ItemArt), and the rarity look
 * (RarityEffects) is a choice of the card, not a kind of card.
 */
export function ItemCard(props: ItemCardProps) {
  switch (props.variant) {
    case "drop":
      return <DropCard {...props} />;
    case "owned":
      return <OwnedCard {...props} />;
    case "pull":
      return props.size === "compact" ? <CompactPullCard {...props} /> : <PullCard {...props} />;
  }
}

/**
 * An item a box can hold: art, name, its published chance and its value. Only the chase tier is
 * loud without effects (a gold edge and glow); every other tier is its dot or shape. Its notices
 * (a counted supply, that it can no longer ship) are badges on the art, so every card in a row is
 * one height. With `onSelect` it is a button that opens the item's details; the badges, buttons
 * of their own, sit over it rather than inside, and the details button is described by them.
 */
function DropCard({
  name,
  image,
  shape,
  className,
  tier,
  chance,
  value,
  tierLabel,
  marker,
  notices = [],
  effects = false,
  onSelect,
}: DropItemCardProps) {
  const { active, wake, seed } = useRarityWake();
  const noticesId = useId();
  const chase = tier === "chase";
  const classes = cardStyles({
    radius: "xl",
    // The chase tier's edge is a rarity, which the core card doesn't know: drawn here on no edge.
    edge: chase ? "none" : "default",
    action: onSelect ? "button" : "none",
    className: cn(
      "flex h-full w-full flex-col gap-2 p-2 pb-3 text-left",
      effects && "group/item relative isolate overflow-hidden",
      chase && "border border-rarity-chase shadow-glow-chase",
      chase && onSelect && "hover:border-rarity-chase",
    ),
  });
  const body = (
    <>
      {effects && <RarityEffects tier={tier} seed={seed} active={active} />}
      {effects ? (
        // Inset, so the tier's glow and halftone show round the art; it grows as the card wakes.
        <span className="block px-[16%] pt-[10%] pb-[2%]">
          <ItemArt src={image} alt="" shape={shape} className={EFFECTS_ART_MOTION} />
        </span>
      ) : (
        <ItemArt src={image} alt="" shape={shape} />
      )}
      <div className="flex flex-col gap-1 px-1">
        {effects ? (
          <RarityName
            name={name}
            tier={tier}
            active={active}
            className="line-clamp-1 font-body text-body-b2-strong text-text-primary"
          />
        ) : (
          <span className="line-clamp-1 font-body text-body-b2-strong text-text-primary">
            {name}
          </span>
        )}
        <span className="flex items-center justify-between gap-2">
          <OddsTag tier={tier} chance={chance} tierLabel={tierLabel} marker={marker} />
          <Amount cents={value} size="meta" trimZeroCents className="text-text-primary" />
        </span>
      </div>
    </>
  );
  const awake = effects ? wake : undefined;
  // The details button is described by the badges over it; a static card has none to describe.
  const described = onSelect && notices.length > 0 ? noticesId : undefined;
  return (
    <div className={cn("relative flex h-full w-full", className)}>
      {onSelect ? (
        <button
          type="button"
          {...awake}
          aria-haspopup="dialog"
          aria-describedby={described}
          onClick={onSelect}
          className={classes}
        >
          {body}
        </button>
      ) : (
        <div {...awake} className={classes}>
          {body}
        </div>
      )}
      {notices.length > 0 && (
        <>
          {described && (
            <span id={described} className="sr-only">
              {notices.map((notice) => notice.label).join(", ")}
            </span>
          )}
          <div className="absolute top-3 right-3 flex max-w-[calc(100%-1.5rem)] flex-col items-end gap-1">
            {notices.map((notice) => (
              <NoticeBadge key={notice.label} {...notice} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * An item the shopper holds: art, name and value, or what became of it. Pickable, the whole card
 * is a toggle (aria-pressed) with a check in its corner, filled and on the accent edge while
 * picked; static, it is plain text, because there is nothing to do with an item already sold or on
 * its way. The name holds two lines whether it needs them or not, and an issue sits on the art,
 * so every card in a row is the same height.
 */
function OwnedCard({
  name,
  image,
  shape = "square",
  currency,
  className,
  note,
  issue,
  faded = false,
  selection,
}: OwnedItemCardProps) {
  const issueId = useId();
  const selected = selection?.selected ?? false;
  const surface = cardStyles({
    radius: "xl",
    edge: selected ? "selected" : "default",
    action: selection ? "button" : "none",
    className: "relative flex h-full w-full flex-col gap-2 p-2 pb-3 text-left",
  });
  const body = (
    <>
      <span className="block rounded-lg bg-bg-inset p-2">
        <ItemArt src={image} alt="" shape={shape} className={cn(faded && "opacity-50")} />
      </span>
      <span className="flex flex-col gap-1 px-1">
        <span className="line-clamp-2 min-h-[2lh] font-body text-body-b2-strong text-text-primary">
          {name}
        </span>
        {note.kind === "value" ? (
          <Amount
            cents={note.cents}
            currency={currency}
            size="meta"
            className="text-text-secondary"
          />
        ) : (
          <span className="font-mono text-mono-meta text-text-secondary">{note.text}</span>
        )}
      </span>
      {selection && (
        <span
          aria-hidden
          className={cn(
            "absolute top-3 right-3 flex size-5 items-center justify-center rounded-pill border",
            selected
              ? "border-accent-default bg-accent-default text-text-on-accent"
              : "border-border-input bg-bg-inset text-transparent",
          )}
        >
          <Icon icon={checkIcon} size={12} />
        </span>
      )}
    </>
  );
  return (
    <div className={cn("relative flex w-full", className)}>
      {selection ? (
        <button
          type="button"
          aria-pressed={selected}
          aria-describedby={issue ? issueId : undefined}
          onClick={selection.onToggle}
          className={surface}
        >
          {body}
        </button>
      ) : (
        <div className={surface}>{body}</div>
      )}
      {issue && (
        <>
          <span id={issueId} className="sr-only">
            {issue.text}
          </span>
          <div className="absolute top-3 left-3 max-w-[calc(100%-3.5rem)]">
            <NoticeBadge label={issue.label} lines={[issue.text]} />
          </div>
        </>
      )}
    </div>
  );
}

/**
 * A pull worth showing off, dressed in its rarity unless `effects` is off: the art a little over
 * half the card's width so the tier's glow and halftone show round it, then the name, the value
 * and who pulled it. Hovered or focused, the art grows and the rarity moves.
 */
function PullCard({
  name,
  image,
  shape = "card",
  currency,
  className,
  cents,
  tier,
  tierLabel,
  player,
  onSelect,
  href,
  label,
  seed: seedProp,
  effects = true,
  priority = false,
}: PullItemCardProps) {
  const { active, wake, seed } = useRarityWake(seedProp);
  const body = (
    <>
      {effects && <RarityEffects tier={tier} seed={seed} active={active} />}
      <span className="block px-[22%] pt-[14%]">
        <ItemArt
          src={image}
          alt=""
          shape={shape}
          priority={priority}
          width={PULL_ART_WIDTH}
          className={EFFECTS_ART_MOTION}
        />
      </span>
      {/* A space between the parts, so the card's text reads as words (a flex column draws none). */}
      <span className="flex min-w-0 flex-col items-center gap-1 px-3 pt-2 pb-3 text-center">
        {effects ? (
          <RarityName
            name={name}
            tier={tier}
            active={active}
            className="block w-full truncate font-body text-body-b2-strong text-text-primary"
          />
        ) : (
          <span className="block w-full truncate font-body text-body-b2-strong text-text-primary">
            {name}
          </span>
        )}{" "}
        <span className="sr-only">{tierLabel} </span>
        <Amount
          cents={cents}
          currency={currency}
          size="meta"
          className="shrink-0 text-text-primary"
        />
        {player && (
          <>
            {" "}
            <span className="flex min-w-0 max-w-full items-center gap-1.5 font-body text-body-b3 text-text-secondary">
              <Avatar name={player.name} src={player.avatarUrl} size="xs" decorative />
              <span className="truncate">{player.name}</span>
            </span>
          </>
        )}
      </span>
    </>
  );
  const surface = (action: "none" | "button") =>
    cardStyles({
      radius: "xl",
      action,
      className: cn(
        "group/item relative isolate flex w-full flex-col overflow-hidden text-left",
        className,
      ),
    });
  if (href) {
    return (
      <AppLink href={href} {...wake} aria-label={label} className={surface("button")}>
        {body}
      </AppLink>
    );
  }
  if (!onSelect) {
    return (
      <div {...wake} className={surface("none")}>
        {body}
      </div>
    );
  }
  return (
    <button
      type="button"
      {...wake}
      aria-haspopup="dialog"
      aria-label={label}
      onClick={onSelect}
      className={surface("button")}
    >
      {body}
    </button>
  );
}

/** A pull's tier as a dot and its name. */
function TierLine({ tier, tierLabel }: { tier: RarityTier; tierLabel: string }): ReactNode {
  return (
    <span className="inline-flex items-center gap-1.5 font-body text-body-b3 text-text-secondary">
      <span aria-hidden className={cn("size-1.5 shrink-0 rounded-pill", RARITY_FILL[tier])} />
      {tierLabel}
    </span>
  );
}

/**
 * A pull small enough that a strip of them stays a glance: the art, the tier as a dot and its
 * name, and the value; the item's name is in the button's name and the details it opens.
 */
function CompactPullCard({
  name,
  image,
  shape,
  currency,
  className,
  cents,
  tier,
  tierLabel,
  onSelect,
  label,
  seed: seedProp,
  effects = false,
}: PullItemCardProps) {
  const { active, wake, seed } = useRarityWake(seedProp);
  return (
    <button
      type="button"
      {...(effects ? wake : undefined)}
      aria-haspopup="dialog"
      aria-label={label}
      onClick={onSelect}
      className={cardStyles({
        radius: "lg",
        action: "button",
        className: cn(
          "flex w-full flex-col gap-1.5 p-1 pb-1.5",
          effects && "group/item relative isolate overflow-hidden",
          className,
        ),
      })}
    >
      {effects && <RarityEffects tier={tier} seed={seed} active={active} />}
      {effects ? (
        // Inset, so the tier's glow and halftone show round the art; it grows as the card wakes.
        <span className="block px-[24%] pt-[16%] pb-[4%]">
          <ItemArt
            src={image}
            alt=""
            shape={shape}
            width={COMPACT_ART_WIDTH}
            className={EFFECTS_ART_MOTION}
          />
        </span>
      ) : (
        <ItemArt src={image} alt="" shape={shape} width={COMPACT_ART_WIDTH} />
      )}
      {/* A space between the parts, so the card's text reads as words (a flex column draws none). */}
      <span className="flex flex-col gap-0.5 px-0.5">
        {/* The name, read with the card when no `label` names it; the strip shows the tier. */}
        <span className="sr-only">{name} </span>
        <TierLine tier={tier} tierLabel={tierLabel} />{" "}
        <Amount cents={cents} currency={currency} size="meta" className="text-text-primary" />
      </span>
    </button>
  );
}
